import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as React from 'react'
import { useCustomBackgroundRoutes } from '~/src/hooks/useCustomBackgroundRoutes'
import { cn } from '~/src/lib/utils'

const RadixTooltipProvider = TooltipPrimitive.Provider

const RadixTooltip = TooltipPrimitive.Root

const RadixTooltipTrigger = TooltipPrimitive.Trigger

const RadixTooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    arrowClassName?: string
    showArrow?: boolean
  }
>(
  (
    {
      className,
      arrowClassName,
      sideOffset = 5,
      children,
      showArrow = false,
      ...props
    },
    ref
  ) => (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'bg-popover text-popover-foreground z-50 select-none rounded-md px-3 py-1.5 text-sm shadow-md will-change-[transform,opacity]',
        'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out duration-150',
        'data-[state=delayed-open][data-side=top]:slide-in-from-bottom-2 data-[state=closed][data-side=top]:slide-out-to-bottom-2',
        'data-[state=delayed-open][data-side=bottom]:slide-in-from-top-2 data-[state=closed][data-side=bottom]:slide-out-to-top-2',
        'data-[state=delayed-open][data-side=left]:slide-in-from-right-2 data-[state=closed][data-side=left]:slide-out-to-right-2',
        'data-[state=delayed-open][data-side=right]:slide-in-from-left-2 data-[state=closed][data-side=right]:slide-out-to-left-2',
        '[&_svg]:h-2 [&_svg]:w-3',
        className
      )}
      {...props}
    >
      {children}
      {showArrow && (
        <TooltipPrimitive.Arrow
          className={cn('fill-popover', arrowClassName)}
        />
      )}
    </TooltipPrimitive.Content>
  )
)
RadixTooltipContent.displayName = TooltipPrimitive.Content.displayName

// Custom hook for managing exclusive tooltip state
const useExclusiveTooltip = () => {
  const [activeTooltip, setActiveTooltip] = React.useState<{
    id: string
    content: React.ReactNode
    position: { x: number; y: number }
    isHome?: boolean
    hasCustomBackground?: boolean
  } | null>(null)

  const showTooltip = React.useCallback(
    (
      id: string,
      content: React.ReactNode,
      position: { x: number; y: number },
      options?: { isHome?: boolean; hasCustomBackground?: boolean }
    ) => {
      setActiveTooltip({ id, content, position, ...options })
    },
    []
  )

  const hideTooltip = React.useCallback((id: string) => {
    setActiveTooltip((prev) => (prev?.id === id ? null : prev))
  }, [])

  return { activeTooltip, showTooltip, hideTooltip }
}

// Context for sharing tooltip state
const ExclusiveTooltipContext = React.createContext<ReturnType<
  typeof useExclusiveTooltip
> | null>(null)

// Provider component that manages the single tooltip instance
const ExclusiveTooltipProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const tooltipState = useExclusiveTooltip()

  return (
    <ExclusiveTooltipContext.Provider value={tooltipState}>
      <RadixTooltipProvider
        delayDuration={0}
        skipDelayDuration={0}
        disableHoverableContent
      >
        {children}
        {tooltipState.activeTooltip && (
          <RadixTooltip open={true}>
            <RadixTooltipTrigger asChild>
              <div
                className='pointer-events-none fixed'
                style={{
                  left: tooltipState.activeTooltip.position.x,
                  top: tooltipState.activeTooltip.position.y,
                  zIndex: 9999,
                }}
              />
            </RadixTooltipTrigger>
            <RadixTooltipContent
              side='right'
              sideOffset={4}
              className={cn(
                'z-[9999]',
                tooltipState.activeTooltip.isHome
                  ? 'bg-[#363636] text-white' // Home page: always dark mode
                  : tooltipState.activeTooltip.hasCustomBackground
                    ? 'border border-gray-200 bg-white text-black' // Custom background: always light mode
                    : 'bg-popover text-popover-foreground' // Regular pages: responsive to theme
              )}
              hideWhenDetached={false}
            >
              {tooltipState.activeTooltip.content}
            </RadixTooltipContent>
          </RadixTooltip>
        )}
      </RadixTooltipProvider>
    </ExclusiveTooltipContext.Provider>
  )
}

// Hook to use the exclusive tooltip
const useExclusiveTooltipContext = () => {
  const context = React.useContext(ExclusiveTooltipContext)
  if (!context) {
    throw new Error(
      'useExclusiveTooltipContext must be used within ExclusiveTooltipProvider'
    )
  }
  return context
}

// Helper function to hide all tooltips
const useHideAllTooltips = () => {
  const context = React.useContext(ExclusiveTooltipContext)

  return React.useCallback(() => {
    if (context?.activeTooltip?.id) {
      context.hideTooltip(context.activeTooltip.id)
    }
  }, [context])
}

// Component for tooltip triggers
const ExclusiveTooltipTrigger: React.FC<{
  id: string
  content: React.ReactNode
  children: React.ReactNode
  className?: string
  isHome?: boolean
}> = ({ id, content, children, className, isHome }) => {
  const { showTooltip, hideTooltip } = useExclusiveTooltipContext()
  const hasCustomBackground = useCustomBackgroundRoutes()

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    showTooltip(
      id,
      content,
      {
        x: rect.right + 8,
        y: rect.top + rect.height / 2,
      },
      { isHome, hasCustomBackground }
    )
  }

  const handleMouseLeave = () => {
    hideTooltip(id)
  }

  return (
    <div
      className={cn('dupe-tooltip-trigger', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  )
}

export {
  ExclusiveTooltipProvider,
  ExclusiveTooltipTrigger,
  useExclusiveTooltipContext,
  useHideAllTooltips,
}
